import React, { useEffect, useState } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { messageError } from '../../../../utils/define';
import CreatableSelect from 'react-select/creatable';
import { useQuery, useMutation } from 'react-query';
import { CME_ADMIN_API } from '../../../../utils/apiUrl';
import { Get, Post } from '../../../../utils/httpProvider';
import { toast } from 'react-toastify';

const CMEConfigAddEditModal = ({ isOpen, handleClose, handleSave, data, type }) => {
    
    // Tạo schema validation động
    const schema = yup.object().shape({
        ma: yup.string().required(messageError.emptyMessage),
        ten: yup.string().required(messageError.emptyMessage),
    });

    const [vaiTroOptions, setVaiTroOptions] = useState([]);
    const [donViTinhOptions, setDonViTinhOptions] = useState([]);

    // Hàm tạo mã viết tắt từ tên (VD: Tham gia các khóa đào tạo -> TGCKDT)
    const generateAbbreviation = (text) => {
        if (!text) return '';
        // Tách từ, lấy chữ cái đầu của mỗi từ và viết hoa
        const words = text.trim().split(/\s+/);
        return words.map(word => word[0]).join('').toUpperCase();
    };

    // Fetch Roles and Units
    useQuery(
        ['cmeVaiTrosData'],
        () => Get(CME_ADMIN_API.GET_CME_VAITRO),
        {
            enabled: isOpen && type === 'rule',
            onSuccess: (res) => {
                const list = res?.$values || res || [];
                setVaiTroOptions(list.map(x => ({ label: x.tenVaiTro, value: x.tenVaiTro })));
            }
        }
    );

    useQuery(
        ['cmeDonViTinhsData'],
        () => Get(CME_ADMIN_API.GET_CME_DONVITINH),
        {
            enabled: isOpen && type === 'rule',
            onSuccess: (res) => {
                const list = res?.$values || res || [];
                setDonViTinhOptions(list.map(x => ({ label: x.tenDonViTinh, value: x.tenDonViTinh })));
            }
        }
    );

    const upsertVaiTroFunc = useMutation((payload) => Post(CME_ADMIN_API.UPSERT_CME_VAITRO, null, payload));
    const upsertDonViTinhFunc = useMutation((payload) => Post(CME_ADMIN_API.UPSERT_CME_DONVITINH, null, payload));

    const handleCreateVaiTro = (inputValue, setFieldValue) => {
        const payload = { tenVaiTro: inputValue };
        upsertVaiTroFunc.mutate(payload, {
            onSuccess: (res) => {
                const newOption = { label: inputValue, value: inputValue };
                setVaiTroOptions((prev) => [...prev, newOption]);
                setFieldValue('vaiTro', inputValue);
                toast.success(`Đã thêm mới vai trò: ${inputValue}`);
            },
            onError: () => toast.error('Thêm mới vai trò thất bại.')
        });
    };

    const handleCreateDonViTinh = (inputValue, setFieldValue) => {
        const payload = { tenDonViTinh: inputValue };
        upsertDonViTinhFunc.mutate(payload, {
            onSuccess: (res) => {
                const newOption = { label: inputValue, value: inputValue };
                setDonViTinhOptions((prev) => [...prev, newOption]);
                setFieldValue('donViTinh', inputValue);
                toast.success(`Đã thêm mới đơn vị tính: ${inputValue}`);
            },
            onError: () => toast.error('Thêm mới đơn vị tính thất bại.')
        });
    };

    return (
        <Modal 
            show={isOpen} 
            onHide={handleClose} 
            centered 
            backdrop="static" 
            keyboard={false} 
            style={{ zIndex: '9999' }}
        >
            <Modal.Header closeButton className="bg-primary text-white">
                <Modal.Title style={{ fontSize: '1.1rem' }}>
                    {data ? 'Cập nhật' : 'Thêm mới'} {type === 'category' ? 'Danh mục' : 'Tiểu mục/Quy định'}
                </Modal.Title>
            </Modal.Header>
            <Formik
                validationSchema={schema}
                initialValues={{
                    ma: type === 'category' ? (data?.maDanhMuc || '') : (data?.maTieuMuc || ''),
                    ten: type === 'category' ? (data?.tenDanhMuc || '') : (data?.tenTieuMuc || ''),
                    moTa: data?.ghiChu || '',
                    soGioToiDa: data?.dinhMucGio || 0,
                    vaiTro: data?.vaiTro || '',
                    donViTinh: data?.donViTinh || '',
                    isAnnualRenewal: data?.isAnnualRenewal || data?.IsAnnualRenewal || false
                }}
                enableReinitialize
                onSubmit={(values) => handleSave(values)}
            >
                {({ handleSubmit, handleChange, setFieldValue, values, errors, touched }) => (
                    <Form noValidate onSubmit={handleSubmit}>
                        <Modal.Body>
                            {/* Ẩn mã, tự động sinh từ tên */}
                            <div style={{ display: 'none' }}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="font-weight-bold">Mã</Form.Label>
                                    <Form.Control
                                        name="ma"
                                        value={values.ma}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </div>

                            <Form.Group className="mb-3">
                                <Form.Label className="font-weight-bold">Tên {type === 'category' ? 'danh mục' : 'tiểu mục'} <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    name="ten"
                                    value={values.ten}
                                    onChange={(e) => {
                                        const newVal = e.target.value;
                                        setFieldValue('ten', newVal);
                                        // Tự động sinh mã khi tạo mới
                                        if (!data) {
                                            setFieldValue('ma', generateAbbreviation(newVal));
                                        }
                                    }}
                                    isInvalid={touched.ten && !!errors.ten}
                                />
                                <Form.Control.Feedback type="invalid">{errors.ten}</Form.Control.Feedback>
                            </Form.Group>

                            {type === 'rule' && (
                                <>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="font-weight-bold">Vai trò</Form.Label>
                                        <CreatableSelect
                                            isClearable
                                            placeholder="Chọn hoặc gõ để thêm..."
                                            formatCreateLabel={(inputValue) => `Tạo mới "${inputValue}"`}
                                            options={vaiTroOptions}
                                            value={vaiTroOptions.find(opt => opt.value === values.vaiTro) || (values.vaiTro ? { label: values.vaiTro, value: values.vaiTro } : null)}
                                            onChange={(selectedOption) => setFieldValue('vaiTro', selectedOption ? selectedOption.value : '')}
                                            onCreateOption={(inputValue) => handleCreateVaiTro(inputValue, setFieldValue)}
                                            isDisabled={upsertVaiTroFunc.isLoading}
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label className="font-weight-bold">Đơn vị tính</Form.Label>
                                        <CreatableSelect
                                            isClearable
                                            placeholder="Chọn hoặc gõ để thêm..."
                                            formatCreateLabel={(inputValue) => `Tạo mới "${inputValue}"`}
                                            options={donViTinhOptions}
                                            value={donViTinhOptions.find(opt => opt.value === values.donViTinh) || (values.donViTinh ? { label: values.donViTinh, value: values.donViTinh } : null)}
                                            onChange={(selectedOption) => setFieldValue('donViTinh', selectedOption ? selectedOption.value : '')}
                                            onCreateOption={(inputValue) => handleCreateDonViTinh(inputValue, setFieldValue)}
                                            isDisabled={upsertDonViTinhFunc.isLoading}
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label className="font-weight-bold">Định mức tính (Số giờ/Đơn vị) <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="soGioToiDa"
                                            value={values.soGioToiDa}
                                            onChange={handleChange}
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Check 
                                            type="checkbox"
                                            id="isAnnualRenewal"
                                            name="isAnnualRenewal"
                                            label="Cho phép gia hạn hàng năm (Dành cho Hướng dẫn luận án...)"
                                            checked={values.isAnnualRenewal}
                                            onChange={(e) => setFieldValue('isAnnualRenewal', e.target.checked)}
                                            className="font-weight-bold text-primary"
                                        />
                                    </Form.Group>

                                    <Form.Group>
                                        <Form.Label className="font-weight-bold">Mô tả (Ghi chú)</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            name="moTa"
                                            value={values.moTa}
                                            onChange={handleChange}
                                        />
                                    </Form.Group>
                                </>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleClose}>Đóng</Button>
                            <Button variant="primary" type="submit">Lưu lại</Button>
                        </Modal.Footer>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};

export default CMEConfigAddEditModal;
